import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { HsReportsSection } from "./HsReportsSection";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "./ui/select";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Upload, Loader2, Search, Trash2, CalendarDays, Layers, Route, TrendingUp,
  Car, Bike, Trophy, ArrowDownWideNarrow, CalendarRange, Archive, ChevronDown, Printer, Gauge,
  FileSpreadsheet, Fuel, Users, PackageCheck,
} from "lucide-react";
import { api } from "../lib/api";
import { distancesExportUrl } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { platformStyle, usePlatform, UNIFIED } from "../lib/ridersPlatform";
import { RiderPlatformSwitcher } from "./RiderPlatformSwitcher";
import { RiderDayRange } from "./RiderDayRange";

const DEFAULT_PLATFORM = "هنجرستيشن";
const CUMULATIVE = "__cumulative__";
const CUMULATIVE_ALL = "__cumulative_all__";
const today = () => new Date().toISOString().slice(0, 10);

const AR_MONTHS = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
const monthLabel = (ym) => {
  const [y, m] = String(ym).split("-");
  return `${AR_MONTHS[parseInt(m, 10) - 1] || m} ${y}`;
};
const monthBounds = (ym) => {
  if (!ym) return [null, null];
  const [y, m] = ym.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return [`${ym}-01`, `${ym}-${String(last).padStart(2, "0")}`];
};
const groupByMonth = (dates) => {
  const map = new Map();
  for (const d of dates) {
    const ym = d.slice(0, 7);
    if (!map.has(ym)) map.set(ym, []);
    map.get(ym).push(d);
  }
  return [...map.entries()];
};

const km = (v) => `${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 1 })} كم`;
const num = (v) => Number(v || 0).toLocaleString("en-US");
const payablePct = (p, tot) => (tot > 0 ? Math.round((Number(p || 0) / Number(tot)) * 1000) / 10 : 0);
const pctTone = (v) => (v >= 85 ? "text-emerald-600" : v >= 65 ? "text-amber-600" : "text-rose-600");
const riderName = (r) => r.emp_name || r.rider_name || r.rider_id;
const VEHICLE_META = {
  car: { label: "سيارة", icon: Car, cls: "text-sky-600", bg: "bg-sky-50", border: "border-sky-200" },
  bike: { label: "دراجة نارية", icon: Bike, cls: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200" },
  unknown: { label: "غير محدّد", icon: Route, cls: "text-slate-500", bg: "bg-slate-50", border: "border-slate-200" },
};

const UploadDialog = ({ open, onOpenChange, onDone, platform }) => {
  const { dir } = useLang();
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState(today());
  const fileRef = useRef(null);

  useEffect(() => { if (open) { setDate(today()); if (fileRef.current) fileRef.current.value = ""; } }, [open]);

  const submit = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.warning("اختر ملف Excel للمسافات"); return; }
    setBusy(true);
    try {
      const res = await api.uploadDistances(file, date, platform);
      toast.success(`تم رفع المسافات — ${res.inserted} مندوب لتاريخ ${res.report_date}`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر رفع الملف");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md font-body" dir={dir} data-testid="distances-upload-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Route className="h-5 w-5 text-indigo-600" /> رفع تقرير المسافات
          </DialogTitle>
          <DialogDescription className="text-right">
            ارفع ملف المسافات اليومي من هنجرستيشن (الأعمدة: Rider Id، Total KM، Payable KM، Completed Deliveries).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="dist-date">تاريخ التقرير</Label>
            <Input id="dist-date" type="date" data-testid="distances-date-input" value={date}
              onChange={(e) => setDate(e.target.value)} max={today()} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dist-file">ملف Excel</Label>
            <Input id="dist-file" type="file" ref={fileRef} data-testid="distances-file-input"
              accept=".xlsx,.xls,.csv,text/csv,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/octet-stream" className="cursor-pointer" />
          </div>
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="confirm-distances-upload-button" disabled={busy} onClick={submit}
            className="bg-indigo-600 hover:bg-indigo-700 gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} رفع
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>إلغاء</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const KPI_TONES = {
  indigo: { icon: "text-indigo-500", val: "text-indigo-700" },
  emerald: { icon: "text-emerald-500", val: "text-emerald-700" },
  sky: { icon: "text-sky-500", val: "text-sky-700" },
  slate: { icon: "text-slate-500", val: "text-slate-700" },
  gold: { icon: "text-amber-500", val: "text-amber-600" },
};
const Kpi = ({ icon: Icon, label, value, tone = "slate", testid }) => {
  const c = KPI_TONES[tone] || KPI_TONES.slate;
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`h-4 w-4 ${c.icon}`} />
        <span className="text-xs text-slate-500">{label}</span>
      </div>
      <p className={`text-2xl font-bold tabular-nums ${c.val}`}>{value}</p>
    </div>
  );
};

const ExtremeCard = ({ kind, row }) => {
  if (!row) return null;
  const top = kind === "top";
  const v = VEHICLE_META[row.vehicle || "unknown"];
  const VIcon = v.icon;
  return (
    <div data-testid={`distance-extreme-${kind}`}
      className={`rounded-2xl border p-4 ${top ? "border-emerald-200 bg-emerald-50/60" : "border-rose-200 bg-rose-50/60"}`}>
      <div className="flex items-center gap-1.5 mb-2">
        {top ? <Trophy className="h-4 w-4 text-emerald-600" /> : <ArrowDownWideNarrow className="h-4 w-4 text-rose-600" />}
        <span className={`text-xs font-semibold ${top ? "text-emerald-700" : "text-rose-700"}`}>{top ? "الأعلى مسافة" : "الأقل مسافة"}</span>
      </div>
      <p className="font-heading font-bold text-slate-800 truncate">{riderName(row)}</p>
      <p className="text-[11px] text-slate-400 font-mono-nums mb-2">#{row.rider_id}</p>
      <div className="flex items-baseline gap-2">
        <span className={`text-2xl font-bold tabular-nums ${top ? "text-emerald-700" : "text-rose-700"}`}>{km(row.payable_km)}</span>
        <span className="text-[11px] text-slate-400">مستحقة</span>
      </div>
      <div className="mt-2 flex items-center gap-2 text-xs text-slate-500 flex-wrap">
        <span className="inline-flex items-center gap-1"><VIcon className={`h-3.5 w-3.5 ${v.cls}`} /> {v.label}</span>
        <span>· {num(row.completed_deliveries)} طلب</span>
        <span>· متوسط {km(row.avg_km)}/طلب</span>
      </div>
    </div>
  );
};

export const RidersDistancesPanel = ({ canEdit }) => {
  const { dir } = useLang();
  const [platformRaw, setPlatformState] = usePlatform();
  const platform = platformRaw && platformRaw !== UNIFIED ? platformRaw : DEFAULT_PLATFORM;
  useEffect(() => { if (!platformRaw || platformRaw === UNIFIED) setPlatformState(DEFAULT_PLATFORM); }, [platformRaw, setPlatformState]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(CUMULATIVE);
  const [range, setRange] = useState({ from: "", to: "" });
  const [search, setSearch] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteMonthOpen, setDeleteMonthOpen] = useState(false);
  const [deleteDayOpen, setDeleteDayOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const isMonthPeriod = period.startsWith("month:");
  const mode = (period === CUMULATIVE || period === CUMULATIVE_ALL || isMonthPeriod) ? "cumulative" : "date";
  const scope = period === CUMULATIVE_ALL ? "all" : "month";
  const dateParam = mode === "date" ? period : null;
  const [effFrom, effTo] = isMonthPeriod ? monthBounds(period.slice(6)) : [range.from || null, range.to || null];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.distancesOverview(mode, dateParam, effFrom, effTo, platform, scope);
      setData(res);
    } catch { toast.error("تعذّر تحميل بيانات المسافات"); }
    finally { setLoading(false); }
  }, [mode, dateParam, scope, effFrom, effTo, platform]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPeriod(CUMULATIVE); }, [platform]);

  const afterUpload = () => { setPeriod(CUMULATIVE); setRange({ from: "", to: "" }); load(); };

  const doDeleteMonth = async () => {
    try {
      const res = await api.deleteDistancesMonth(period.slice(6), platform);
      toast.success(`تم حذف مسافات شهر ${monthLabel(period.slice(6))} (${res.deleted} صف)`);
      setDeleteMonthOpen(false); setPeriod(CUMULATIVE); load();
    } catch { toast.error("تعذّر الحذف"); }
  };
  const doDeleteDay = async () => {
    try {
      const res = await api.deleteDistancesDate(dateParam, platform);
      toast.success(`تم حذف مسافات ${dateParam} (${res.deleted} صف)`);
      setDeleteDayOpen(false); setPeriod(CUMULATIVE); load();
    } catch { toast.error("تعذّر الحذف"); }
  };

  const rows = useMemo(() => data?.rows || [], [data]);
  const filteredRows = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => [r.emp_name, r.rider_id, r.city].some((v) => String(v || "").toLowerCase().includes(s)));
  }, [rows, search]);

  const sum = data?.summary;
  const vcompare = data?.vehicle_compare || [];
  const feff = data?.fuel_efficiency || [];
  const extremes = data?.extremes;
  const hasData = (data?.available_dates || []).length > 0;
  const cumulative = mode === "cumulative";
  const ps = platformStyle(platform);
  const exportUrl = distancesExportUrl(mode, dateParam, effFrom, effTo, platform, scope);
  const avgCostPerKm = useMemo(() => {
    const vals = rows.map((r) => r.cost_per_km).filter((v) => typeof v === "number" && v > 0);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
  }, [rows]);

  const printPdf = () => {
    if (!filteredRows.length) { toast.warning("لا توجد بيانات للطباعة"); return; }
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const periodLabel = cumulative
      ? (isMonthPeriod ? `شهر (${monthLabel(period.slice(6))})` : scope === "all" ? "كل الأيام" : "الشهر الحالي")
      : dateParam;
    const body = filteredRows.map((r, i) => `<tr><td>${i + 1}</td><td class="rider">${esc(riderName(r))} <span class="rid">#${esc(r.rider_id)}</span></td><td>${esc(VEHICLE_META[r.vehicle || "unknown"].label)}</td><td>${esc(r.city || "—")}</td><td class="num">${esc(km(r.total_km))}</td><td class="num">${esc(km(r.payable_km))}</td><td class="num">${payablePct(r.payable_km, r.total_km)}%</td><td class="num">${esc(km(r.avg_km))}</td><td class="num">${num(r.completed_deliveries)}</td></tr>`).join("");
    const html = `<!doctype html><html dir="rtl" lang="ar"><head><style>@font-face{font-family:SaudiRiyal;src:url(/fonts/riyal.woff2) format("woff2");unicode-range:U+20C1}</style><meta charset="utf-8"><title>تقرير المسافات — ${esc(platform)}</title>
      <style>@page{size:A4 landscape;margin:10mm;}body{font-family:SaudiRiyal,'IBM Plex Sans Arabic','Arial',sans-serif;color:#0f172a;}h1{font-size:16px;margin:0 0 2px;text-align:center;}.sub{text-align:center;font-size:11px;color:#475569;margin-bottom:10px;}table{width:100%;border-collapse:collapse;font-size:10px;}th,td{border:1px solid #cbd5e1;padding:4px 6px;text-align:center;white-space:nowrap;}th{background:#eef2ff;}.rider{text-align:right;}.rid{color:#94a3b8;font-size:8px;}.num{font-variant-numeric:tabular-nums;}</style></head>
      <body><h1>تقرير المسافات — ${esc(platform)}</h1><div class="sub">${esc(periodLabel)} · ${filteredRows.length} مندوب · إجمالي ${esc(km(sum?.total_km))}</div>
      <table><thead><tr><th>#</th><th>المندوب</th><th>المركبة</th><th>المدينة</th><th>إجمالي الكيلومترات</th><th>المستحقة</th><th>نسبة المستحق %</th><th>المتوسط/طلب</th><th>الطلبات</th></tr></thead><tbody>${body}</tbody></table>
      <script>window.onload=function(){window.print();}</script></body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="distances-panel" translate="no">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2.5">
          <div className="h-11 w-11 rounded-2xl grid place-items-center shadow-sm" style={{ background: ps.bg }}>
            <Route className="h-5 w-5" style={{ color: ps.text }} />
          </div>
          <div>
            <h2 className="font-heading font-bold text-xl text-slate-800 flex items-center gap-2">مسافات المندوبين</h2>
            <p className="text-sm text-slate-400">{platform} · الكيلومترات المقطوعة والمستحقة</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasData && (
            <>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger data-testid="distance-period-select" className="w-48 gap-1.5">
                  {cumulative ? <Layers className="h-4 w-4 text-indigo-600" /> : <CalendarDays className="h-4 w-4 text-slate-400" />}
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CUMULATIVE} data-testid="distance-period-cumulative">تراكمي — الشهر الحالي</SelectItem>
                  <SelectItem value={CUMULATIVE_ALL} data-testid="distance-period-cumulative-all">تراكمي — كل الأيام</SelectItem>
                  {(() => {
                    const all = [...(data.available_dates || [])].reverse();
                    if (!all.length) return null;
                    const latestMonth = all[0].slice(0, 7);
                    const current = all.filter((d) => d.slice(0, 7) === latestMonth);
                    const older = all.filter((d) => d.slice(0, 7) !== latestMonth);
                    return (
                      <>
                        <SelectGroup>
                          <SelectLabel className="text-[11px] text-slate-400 font-normal">{monthLabel(latestMonth)}</SelectLabel>
                          {current.map((d) => <SelectItem key={d} value={d}>{`يوم ${d}`}</SelectItem>)}
                        </SelectGroup>
                        {older.length > 0 && (
                          <>
                            <div role="button" tabIndex={0} data-testid="distance-archive-toggle"
                              onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setArchiveOpen((o) => !o); }}
                              className="flex items-center justify-between gap-2 px-2 py-2 mt-1 border-t border-slate-100 text-xs font-medium text-slate-500 cursor-pointer hover:bg-slate-50 rounded-md select-none">
                              <span className="flex items-center gap-1.5"><Archive className="h-3.5 w-3.5" /> الأرشيف — أشهر سابقة ({older.length})</span>
                              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${archiveOpen ? "rotate-180" : ""}`} />
                            </div>
                            {archiveOpen && groupByMonth(older).map(([m, ds]) => (
                              <SelectGroup key={m}>
                                <SelectLabel className="text-[11px] text-slate-400 font-normal">{monthLabel(m)}</SelectLabel>
                                {ds.map((d) => <SelectItem key={d} value={d}>{`يوم ${d}`}</SelectItem>)}
                              </SelectGroup>
                            ))}
                          </>
                        )}
                      </>
                    );
                  })()}
                </SelectContent>
              </Select>
              {(() => {
                const months = [...new Set((data.available_dates || []).map((d) => d.slice(0, 7)))].sort().reverse();
                if (!months.length) return null;
                return (
                  <Select value={isMonthPeriod ? period : ""} onValueChange={setPeriod}>
                    <SelectTrigger data-testid="distance-month-select" className="w-40 gap-1.5">
                      <CalendarRange className="h-4 w-4 text-amber-500" />
                      <SelectValue placeholder="اختر شهراً" />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((m) => <SelectItem key={m} value={`month:${m}`} data-testid={`distance-month-${m}`}>{monthLabel(m)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                );
              })()}
              {cumulative && !isMonthPeriod && (
                <RiderDayRange dates={data.available_dates} range={range} setRange={setRange} />
              )}
              <Button variant="outline" size="sm" data-testid="distances-print-button" className="gap-1.5" onClick={printPdf}>
                <Printer className="h-4 w-4" /> <span className="hidden sm:inline">PDF</span>
              </Button>
              <a href={exportUrl} data-testid="distances-excel-button"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm font-medium transition-colors">
                <FileSpreadsheet className="h-4 w-4" /> <span className="hidden sm:inline">Excel</span>
              </a>
              {canEdit && !cumulative && (
                <Button variant="outline" size="sm" data-testid="delete-distance-date-button"
                  className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50" onClick={() => setDeleteDayOpen(true)}>
                  <Trash2 className="h-4 w-4" /> <span className="hidden sm:inline">حذف اليوم</span>
                </Button>
              )}
              {canEdit && isMonthPeriod && (
                <Button variant="outline" size="sm" data-testid="delete-distance-month-button"
                  className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50" onClick={() => setDeleteMonthOpen(true)}>
                  <Trash2 className="h-4 w-4" /> <span className="hidden sm:inline">حذف الشهر</span>
                </Button>
              )}
            </>
          )}
          {canEdit && (
            <Button size="sm" data-testid="upload-distances-button" className="bg-indigo-600 hover:bg-indigo-700 gap-1.5"
              onClick={() => setUploadOpen(true)}>
              <Upload className="h-4 w-4" /> رفع المسافات
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 mb-4 flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs font-semibold text-slate-500">منصة التوصيل</p>
        <RiderPlatformSwitcher />
      </div>

      {loading ? (
        <div className="py-24 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>
      ) : !hasData ? (
        <div className="py-20 text-center" data-testid="distances-empty">
          <Route className="h-12 w-12 mx-auto text-slate-300 mb-3" />
          <p className="font-heading font-bold text-slate-700">لا توجد بيانات مسافات بعد</p>
          <p className="text-sm text-slate-400 mt-1">ارفع ملف المسافات اليومي من هنجرستيشن للبدء.</p>
          {canEdit && (
            <Button className="mt-4 bg-indigo-600 hover:bg-indigo-700 gap-1.5" onClick={() => setUploadOpen(true)}>
              <Upload className="h-4 w-4" /> رفع المسافات
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-5">
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Kpi icon={Route} label="إجمالي الكيلومترات" value={km(sum?.total_km)} tone="indigo" testid="kpi-total-km" />
            <Kpi icon={Gauge} label="الكيلومترات المستحقة" value={km(sum?.payable_km)} tone="emerald" testid="kpi-payable-km" />
            <Kpi icon={TrendingUp} label="المتوسط لكل طلب" value={km(sum?.avg_km)} tone="sky" testid="kpi-avg-km" />
            <Kpi icon={PackageCheck} label="الطلبات المكتملة" value={num(sum?.completed_deliveries)} tone="gold" testid="kpi-completed" />
            <Kpi icon={Users} label="عدد المندوبين" value={num(sum?.total_riders)} tone="slate" testid="kpi-riders" />
          </div>

          {/* Highest vs Lowest */}
          {extremes && (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="distance-extremes">
              <ExtremeCard kind="top" row={extremes.highest} />
              <ExtremeCard kind="low" row={extremes.lowest} />
            </div>
          )}

          {/* Vehicle comparison */}
          {vcompare.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="vehicle-compare">
              <p className="font-heading font-bold text-slate-700 text-sm mb-3 flex items-center gap-1.5">
                <Car className="h-4 w-4 text-sky-600" /><Bike className="h-4 w-4 text-orange-600" /> مقارنة السيارات والدراجات النارية
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-right min-w-[560px]">
                  <thead>
                    <tr className="bg-slate-50 border-y border-slate-200 text-xs text-slate-600">
                      <th className="px-3 py-2 font-semibold">نوع المركبة</th>
                      <th className="px-3 py-2 font-semibold">المندوبون</th>
                      <th className="px-3 py-2 font-semibold">إجمالي الكيلومترات</th>
                      <th className="px-3 py-2 font-semibold">المستحقة</th>
                      <th className="px-3 py-2 font-semibold">الطلبات</th>
                      <th className="px-3 py-2 font-semibold">المتوسط/طلب</th>
                      <th className="px-3 py-2 font-semibold">المتوسط/مندوب</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vcompare.map((v) => {
                      const meta = VEHICLE_META[v.vehicle] || VEHICLE_META.unknown;
                      const VIcon = meta.icon;
                      return (
                        <tr key={v.vehicle} data-testid={`vehicle-row-${v.vehicle}`} className="border-b border-slate-100 last:border-0">
                          <td className="px-3 py-2.5">
                            <span className={`inline-flex items-center gap-1.5 font-semibold ${meta.cls}`}>
                              <VIcon className="h-4 w-4" /> {meta.label}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 tabular-nums">{num(v.riders)}</td>
                          <td className="px-3 py-2.5 tabular-nums font-semibold text-slate-800">{km(v.total_km)}</td>
                          <td className="px-3 py-2.5 tabular-nums text-emerald-700">{km(v.payable_km)}</td>
                          <td className="px-3 py-2.5 tabular-nums">{num(v.completed_deliveries)}</td>
                          <td className="px-3 py-2.5 tabular-nums">{km(v.avg_km)}</td>
                          <td className="px-3 py-2.5 tabular-nums">{km(v.avg_km_per_rider)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Fuel efficiency: KM vs fuel cost per vehicle type */}
          {feff.length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4" data-testid="fuel-efficiency">
              <p className="font-heading font-bold text-slate-700 text-sm mb-1 flex items-center gap-1.5">
                <Fuel className="h-4 w-4 text-amber-600" /> كفاءة الوقود — المسافة مقابل التكلفة
              </p>
              <p className="text-[11px] text-slate-400 mb-3">
                مقارنة الكيلومترات المقطوعة بتكلفة الوقود لكل نوع مركبة{data?.covered_months?.length ? ` · وقود شهر ${data.covered_months.map(monthLabel).join("، ")}` : ""} (للمندوبين المرتبطين بموظفين لديهم سجل وقود).
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {feff.map((e) => {
                  const meta = VEHICLE_META[e.vehicle] || VEHICLE_META.unknown;
                  const VIcon = meta.icon;
                  return (
                    <div key={e.vehicle} data-testid={`fuel-eff-${e.vehicle}`} className={`rounded-xl border ${meta.border} bg-white p-3`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`inline-flex items-center gap-1.5 font-semibold ${meta.cls}`}><VIcon className="h-4 w-4" /> {meta.label}</span>
                        <span className="text-[11px] text-slate-400">{num(e.riders)} مندوب</span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                        <div className="rounded-lg bg-slate-50 p-2 text-center">
                          <p className="font-bold tabular-nums text-slate-800">{km(e.total_km)}</p>
                          <p className="text-[10px] text-slate-500">المسافة</p>
                        </div>
                        <div className="rounded-lg bg-slate-50 p-2 text-center">
                          <p className="font-bold tabular-nums text-slate-800">{num(e.fuel_cost)} ⃁</p>
                          <p className="text-[10px] text-slate-500">تكلفة الوقود</p>
                        </div>
                        <div className="rounded-lg bg-amber-50 p-2 text-center">
                          <p className="font-bold tabular-nums text-amber-700">{e.cost_per_km} ⃁</p>
                          <p className="text-[10px] text-slate-500">تكلفة الكيلومتر</p>
                        </div>
                        <div className="rounded-lg bg-emerald-50 p-2 text-center">
                          <p className="font-bold tabular-nums text-emerald-700">{e.km_per_sar} كم</p>
                          <p className="text-[10px] text-slate-500">كم لكل ⃁</p>
                        </div>
                      </div>
                      <p className="text-[11px] text-slate-500 text-center mt-2">تكلفة وقود الطلب الواحد: <span className="font-semibold text-slate-700">{e.cost_per_order} ⃁</span></p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="relative max-w-xs">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="distance-search" placeholder="ابحث باسم المندوب أو الرقم…" value={search}
              onChange={(e) => setSearch(e.target.value)} className="pr-9" />
          </div>

          {/* Main table (desktop) */}
          <div className="hidden md:block rounded-2xl border border-slate-200 bg-white overflow-x-auto" data-testid="distances-table">
            <table className="w-full text-sm text-right min-w-[760px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs text-slate-600">
                  <th className="px-3 py-2.5 font-semibold">#</th>
                  <th className="px-3 py-2.5 font-semibold">المندوب</th>
                  <th className="px-3 py-2.5 font-semibold">المركبة</th>
                  <th className="px-3 py-2.5 font-semibold">المدينة</th>
                  {cumulative && <th className="px-3 py-2.5 font-semibold">الأيام</th>}
                  <th className="px-3 py-2.5 font-semibold">إجمالي الكيلومترات</th>
                  <th className="px-3 py-2.5 font-semibold">المستحقة</th>
                  <th className="px-3 py-2.5 font-semibold">نسبة المستحق %</th>
                  <th className="px-3 py-2.5 font-semibold">المتوسط/طلب</th>
                  <th className="px-3 py-2.5 font-semibold">الطلبات</th>
                  <th className="px-3 py-2.5 font-semibold">تكلفة الكم (⃁)</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r, i) => {
                  const meta = VEHICLE_META[r.vehicle || "unknown"];
                  const VIcon = meta.icon;
                  const cpk = typeof r.cost_per_km === "number" && r.cost_per_km > 0 ? r.cost_per_km : null;
                  const cpkHigh = cpk != null && avgCostPerKm > 0 && cpk > avgCostPerKm;
                  return (
                    <tr key={r.rider_id} data-testid={`distance-row-${r.rider_id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                      <td className="px-3 py-2.5 text-slate-400 tabular-nums">{i + 1}</td>
                      <td className="px-3 py-2.5">
                        <span className="font-semibold text-slate-800">{riderName(r)}</span>
                        <span className="text-[10px] text-slate-400 font-mono-nums block">#{r.rider_id}</span>
                      </td>
                      <td className="px-3 py-2.5"><span className={`inline-flex items-center gap-1 ${meta.cls}`}><VIcon className="h-3.5 w-3.5" /> {meta.label}</span></td>
                      <td className="px-3 py-2.5 text-slate-600">{r.city || "—"}</td>
                      {cumulative && <td className="px-3 py-2.5 tabular-nums text-slate-500">{r.days ?? "—"}</td>}
                      <td className="px-3 py-2.5 tabular-nums font-bold text-indigo-700">{km(r.total_km)}</td>
                      <td className="px-3 py-2.5 tabular-nums text-emerald-700">{km(r.payable_km)}</td>
                      <td className="px-3 py-2.5 tabular-nums" data-testid={`distance-pct-${r.rider_id}`}>
                        {(() => { const v = payablePct(r.payable_km, r.total_km); return <span className={`font-semibold ${pctTone(v)}`}>{v}%</span>; })()}
                      </td>
                      <td className="px-3 py-2.5 tabular-nums">{km(r.avg_km)}</td>
                      <td className="px-3 py-2.5 tabular-nums">{num(r.completed_deliveries)}</td>
                      <td className="px-3 py-2.5 tabular-nums" data-testid={`distance-cpk-${r.rider_id}`}>
                        {cpk == null ? <span className="text-slate-300">—</span>
                          : <span className={`inline-flex items-center gap-1 font-semibold ${cpkHigh ? "text-rose-600" : "text-emerald-600"}`}>
                              {cpkHigh && <TrendingUp className="h-3.5 w-3.5" />}{cpk} ⃁
                            </span>}
                      </td>
                    </tr>
                  );
                })}
                {filteredRows.length === 0 && (
                  <tr><td colSpan={cumulative ? 11 : 10} className="px-3 py-8 text-center text-slate-400">لا يوجد مندوب مطابق</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2.5">
            {filteredRows.map((r, i) => {
              const meta = VEHICLE_META[r.vehicle || "unknown"];
              const VIcon = meta.icon;
              return (
                <div key={r.rider_id} data-testid={`distance-card-${r.rider_id}`} className="rounded-xl border border-slate-200 bg-white p-3">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-2 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="h-6 w-6 shrink-0 rounded-full bg-slate-100 text-slate-500 text-xs grid place-items-center font-bold">{i + 1}</span>
                      <div className="min-w-0">
                        <p className="font-heading font-bold text-slate-800 text-sm truncate">{riderName(r)}</p>
                        <p className="text-[10px] text-slate-400 font-mono-nums">#{r.rider_id}</p>
                      </div>
                    </div>
                    <span className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-bold text-indigo-700 tabular-nums shrink-0">{km(r.total_km)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                    <div className="flex justify-between"><span className="text-slate-500">المركبة</span><span className={`font-semibold inline-flex items-center gap-1 ${meta.cls}`}><VIcon className="h-3.5 w-3.5" />{meta.label}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">المدينة</span><span className="font-semibold">{r.city || "—"}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">المستحقة</span><span className="font-semibold text-emerald-700">{km(r.payable_km)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">نسبة المستحق</span>{(() => { const v = payablePct(r.payable_km, r.total_km); return <span className={`font-semibold ${pctTone(v)}`}>{v}%</span>; })()}</div>
                    <div className="flex justify-between"><span className="text-slate-500">المتوسط/طلب</span><span className="font-semibold">{km(r.avg_km)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">الطلبات</span><span className="font-semibold">{num(r.completed_deliveries)}</span></div>
                    {cumulative && <div className="flex justify-between"><span className="text-slate-500">الأيام</span><span className="font-semibold">{r.days ?? "—"}</span></div>}
                    {typeof r.cost_per_km === "number" && r.cost_per_km > 0 && (
                      <div className="flex justify-between"><span className="text-slate-500">تكلفة الكم</span>
                        <span className={`font-semibold ${avgCostPerKm > 0 && r.cost_per_km > avgCostPerKm ? "text-rose-600" : "text-emerald-600"}`}>{r.cost_per_km} ⃁</span></div>
                    )}
                  </div>
                </div>
              );
            })}
            {filteredRows.length === 0 && <p className="text-center text-slate-400 py-8 text-sm">لا يوجد مندوب مطابق</p>}
          </div>
        </div>
      )}

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onDone={afterUpload} platform={platform} />

      <AlertDialog open={deleteMonthOpen} onOpenChange={setDeleteMonthOpen}>
        <AlertDialogContent dir={dir} className="font-body">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-right">حذف مسافات الشهر</AlertDialogTitle>
            <AlertDialogDescription className="text-right">
              هل تريد حذف كل مسافات شهر «{isMonthPeriod ? monthLabel(period.slice(6)) : ""}»؟ لا يمكن التراجع.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2 sm:justify-start">
            <AlertDialogAction data-testid="confirm-delete-distance-month-button" onClick={doDeleteMonth} className="bg-red-600 hover:bg-red-700">حذف</AlertDialogAction>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteDayOpen} onOpenChange={setDeleteDayOpen}>
        <AlertDialogContent dir={dir} className="font-body">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-right">حذف مسافات اليوم</AlertDialogTitle>
            <AlertDialogDescription className="text-right">
              هل تريد حذف كل مسافات تاريخ «{dateParam}»؟ لا يمكن التراجع.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2 sm:justify-start">
            <AlertDialogAction data-testid="confirm-delete-distance-date-button" onClick={doDeleteDay} className="bg-red-600 hover:bg-red-700">حذف</AlertDialogAction>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <HsReportsSection only={["distances"]} title="تقرير مسافات الدراجين 3PL (مسحوب تلقائياً)" />
    </motion.div>
  );
};
